import {
  Alert,
  Box,
  Button,
  ButtonDropdown,
  Modal,
  SpaceBetween,
} from '@cloudscape-design/components';
import { getPPartId } from '@parallel-fluidics/shared/models/projects/parts';
import * as Sentry from '@sentry/react';
import {
  deleteDoc,
  doc,
  getFirestore,
} from 'firebase/firestore';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { usePPartData } from './pPartProvider';

export default function PartActionButton({ part, variant = 'icon' }) {
  const navigate = useNavigate();
  const {
    setOpenAddModal,
    setEditingPart,
    usedPPartIdsSet,
    project,
    projectDocId,
  } = usePPartData();

  const [deletingItem, setDeletingItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = useCallback((item) => {
    if (!item || !item.id) return;
    setIsDeleting(true);
    const partRef = doc(getFirestore(), 'projects', projectDocId, 'parts', item.id);
    deleteDoc(partRef).then(() => {
      toast.success('Part deleted!');
      // Log deletion in Sentry
      Sentry.logger.info('Part deleted!', { part: item, projectDocId });
    }).catch((error) => {
      toast.error(`Error removing document: ${error.message}`);
      Sentry.captureException(error, { extra: { part: item, projectDocId } });
    }).finally(() => {
      setIsDeleting(false);
      // close the modal
      setDeletingItem(null);
    });
  }, [projectDocId]);

  return (
    <>
      <ButtonDropdown
        items={[
          { id: 'edit', text: 'Edit Part' },
          // Lots only exist for internal projects — no entry on customer ones.
          ...(project?.isInternal ? [{ id: 'lots', text: 'View Lots' }] : []),
          {
            id: 'delete',
            text: 'Delete Part',
            disabled: usedPPartIdsSet.has(getPPartId(part)),
            disabledReason: 'Part is used in orders',
          },
        ]}
        onItemClick={({ detail }) => {
          switch (detail.id) {
            case 'edit':
              setEditingPart(part);
              setOpenAddModal(true);
              break;
            case 'lots':
              navigate(`/lots?projectDocId=${projectDocId}&pPartDocId=${part.id}`);
              break;
            case 'delete':
              // show confirm modal
              setDeletingItem(part);
              break;
            default:
          }
        }}
        expandToViewport
        variant={variant}
      />
      {!!deletingItem && (
        <Modal
          onDismiss={() => setDeletingItem(null)}
          visible
          closeAriaLabel="Close modal"
          header="Confirm Delete"
          footer={(
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="primary" onClick={() => setDeletingItem(null)}>Cancel</Button>
                <Button variant="normal" loading={isDeleting} onClick={() => handleDelete(deletingItem)}>Permanently Delete</Button>
              </SpaceBetween>
            </Box>
            )}
        >
          <SpaceBetween size="m">
            <div>
              Permanently delete
              {' '}
              <b>{getPPartId(deletingItem)}</b>
              ? You can’t undo this action.
            </div>
            <Alert type="warning" header="Caution">
              Please double-check you are deleting the correct part.
              Once deleted, the part data will be lost and cannot be recovered.
            </Alert>
          </SpaceBetween>
        </Modal>
      )}
    </>
  );
}

PartActionButton.propTypes = {
  part: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  variant: PropTypes.oneOf(['icon', 'inline-icon']),
};
